/* `/raporty/katalog` — KANON `06-katalog-pytan.html`. A header, a toolbar, and one card of rows.
 *
 * The card is the canon's own 960px, fixed: it is a list of sentences and a fluid one would set a
 * different measure on every laptop. The rows reserve their height so the skeleton stands inside
 * the same box the answer lands in. */

.header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-4);
}

.headText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.crumb {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  font-size: var(--fs-note);
  color: var(--text-2);
}

.crumbLink {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--space-7);
  height: var(--space-7);
  border-radius: var(--radius-sm);
}

.crumbIcon {
  display: block;
  width: var(--icon-xs);
  height: var(--icon-xs);
}

/* `canon.h1` supplies 28 / 800 / -.03em; this is the heading's own margin reset. */
.titleRow {
  display: flex;
  gap: var(--space-4);
  align-items: center;
  justify-content: space-between;
}

.title {
  margin: var(--space-0);
}

.lede {
  max-width: var(--w-report-shelf);
  margin: var(--space-0);
  font-size: var(--type-control);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.tools {
  display: flex;
  gap: var(--space-2-5px);
  align-items: center;
}

/* The shelf's own field is WIDER than the Ankiety toolbar's — 320 against 240 — and takes the same
 * `.field` cut. Descendant selectors for the reason `ReportsScreen.module.css` gives: `.frame` and
 * a bare `.search` are both single-class, and the winner would be whichever chunk landed second. */
.tools .search {
  width: var(--w-report-shelf-search);
  height: var(--h-report-editor-field);
  border-radius: var(--radius-13px);
}

.toolsIdle {
  visibility: hidden;
}

/* The empty shelf's two lines, inside `EmptyState`'s sentence. */
.emptyTitle {
  display: block;
  font-size: var(--type-body);
  font-weight: var(--fw-semibold);
  color: var(--text);
}

.emptyHint {
  display: block;
  margin-top: var(--space-1);
  font-size: var(--type-control);
  font-weight: var(--fw-regular);
  color: var(--text-2);
}

.toolSpacer {
  flex: 1;
}

.count {
  font-size: var(--fs-note);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

.card {
  display: flex;
  box-sizing: border-box;
  flex-direction: column;

  /* The page's measure, so the card ends where the title row's `+` does (4 November 2026). */
  width: 100%;
  min-height: var(--h-report-picker-list);
  overflow: hidden;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xl);
}

.list {
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

.row {
  display: flex;
  gap: var(--space-14px);
  align-items: center;
  min-height: var(--h-report-queue-row);
  padding: var(--space-14px) var(--space-17px);
  border-top: var(--border-hairline) solid var(--border);
}

.row:first-child {
  border-top: 0;
}

/* The canon's row tile is `--bg-inset`, the same ground every other tile in this canon stands on;
 * `--bg-chip` is a step brighter and made the shelf's rows read louder than the editor's. */
.tile {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: var(--sz-report-shelf-tile);
  height: var(--sz-report-shelf-tile);
  color: var(--text-2);
  background: var(--bg-inset);
  border-radius: var(--radius-sm);
}

.tileIcon {
  display: block;
  width: var(--icon-xs);
  height: var(--icon-xs);
}

.rowText {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.rowLabel {
  overflow: hidden;
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The answer type and the date a question was saved — both read, both at `--fs-micro`, so neither
 * may sit on the tertiary ink. */
.rowMeta {
  display: flex;
  gap: var(--space-2-5px);
  align-items: center;
  font-size: var(--fs-micro);
  color: var(--text-2);
}

.metaIcon {
  display: block;
  flex: none;
  width: var(--sz-report-chip-icon);
  height: var(--sz-report-chip-icon);
}

.tags {
  display: flex;
  flex: none;
  gap: var(--space-2);
  flex-wrap: wrap;
  justify-content: flex-end;
  width: var(--w-report-shelf-tags);
}

/* The canon's chip, at its own 11.5 and radius 14 — this drew 10 at radius 18. */
.tag {
  display: inline-flex;
  align-items: center;
  height: var(--h-report-chip);
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  background: var(--bg-chip);
  border-radius: var(--r-chip);
}

/* „Nieużywane" — the whole of what this cell says about a question, so it is the text rather than a
 * decoration and takes `--text-2`. */
.unused {
  font-size: var(--fs-micro);
  color: var(--text-2);
}

.state {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
}

.banner {
  margin: var(--space-0);
  padding: var(--space-3) var(--space-4);
  font-size: var(--fs-note);
  color: var(--danger-ink);
  background: var(--danger-soft);
  border-radius: var(--radius-md);
}

/* The BAND is the reserve and it is the loaded row's own height, so the gate paints a bar inside it
 * without changing the box — „the gate hides the bars, never the box". */
.skelRow {
  display: flex;
  align-items: center;
  height: var(--h-report-queue-row);
  padding: var(--space-0) var(--space-17px);
}

.skelBar {
  width: 60%;
  height: var(--space-4);
}
